import InfoRounded from '@mui/icons-material/InfoRounded';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography, { type TypographyProps } from '@mui/material/Typography';
import { AttentionOverview } from '@openx/components-targeting/Targeting/Attention/AttentionOverview';
import { DimensionChip } from '@openx/components-targeting/Targeting/shared/DimensionChip/DimensionChip';
import { ExcludeBelowAverageDomains } from '@openx/components-targeting/Targeting/shared/ExcludeBelowAverageDomains/ExcludeBelowAverageDomains';
import { ExcludeIndirect } from '@openx/components-targeting/Targeting/shared/ExcludeIndirect/ExcludeIndirect';
import { ExcludeMFA } from '@openx/components-targeting/Targeting/shared/ExcludeMFA/ExcludeMFA';
import { NoFiltersInfoAlert } from '@openx/components-targeting/Targeting/shared/TemporaryElements/NoFiltersInfoAlert';
import { DisableRboAlert } from '@openx/components-targeting/Targeting/TargetingPresets/RequiredTargetingSection/components/DisableRboAlert/DisableRboAlert';
import { RboDisplayedAlertProvider } from '@openx/components-targeting/Targeting/TargetingPresets/RequiredTargetingSection/components/RboDisplayedAlertContext';
import { RboGroupValidationAlert } from '@openx/components-targeting/Targeting/TargetingPresets/RequiredTargetingSection/components/RboGroupValidationAlert/RboGroupValidationAlert';
import { RequiredTargetingSection } from '@openx/components-targeting/Targeting/TargetingPresets/RequiredTargetingSection/RequiredTargetingSection';
import { useTargetingContext } from '@openx/components-targeting/utils/context/TargetingContext';
import { ALLOWLIST_TARGETING_ATTENTION, ENABLED_TARGETING_ATTENTION } from '@openx/constants/feature-flag-keys';
import { AdPlacement } from '@openx/types/adUnit';
import { DistributionChannel } from '@openx/types/distributionChannel';
import { PackageFields, type PackageFormState } from '@openx/types/package';
import { DomainAppInventoryOperator, UrlTargetingType } from '@openx/types/targeting/domains';
import { InventoryFormatTypeOption } from '@openx/types/targeting/formatType';
import { type TargetingConfig, TargetingFields } from '@openx/types/targeting/targeting';
import type { TargetingAppInventory } from '@openx/types/targeting/targetingAppInventoryTypes';
import { ComparisonType, Criteria, Intersect } from '@openx/types/targeting/targetingValuesTypes';
import { TechnologyAndDevicesOption } from '@openx/types/targeting/technologyAndDevices';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext';
import { OPEN_AUDIENCE_ORG_UID } from '@openx/utils/services/config';
import { Fragment, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { toCustomTargetingParams } from '../../../utils/toCustomTargetingParams';

export type TargetingReadOnlyListProps = {
  targeting: TargetingConfig[];
  packageState: PackageFormState;
  validateLegacyViewFormat?: boolean;
};

const StyledTargetingSubSectionTitle = (props: TypographyProps) => (
  <Typography
    variant="h3"
    {...props}
    sx={[
      {
        width: '100%',
        mb: 1.5,
      },
      ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
    ]}
  />
);

const hasDomainsValues = (domainsTargeting: unknown): boolean => {
  if (!domainsTargeting || typeof domainsTargeting !== 'object') return false;

  const t = domainsTargeting as { inventorylist_id?: string | null; urls?: string[] | null };
  return Boolean(t.inventorylist_id) || Boolean(t.urls?.length);
};

const isDomainsExcludeMode = (domainsTargeting: unknown): boolean => {
  if (!domainsTargeting || typeof domainsTargeting !== 'object') return false;
  const t = domainsTargeting as { type?: UrlTargetingType | string | null };
  return t.type === UrlTargetingType.blacklist;
};

const hasAnyAppInventoryValues = (appInventory: TargetingAppInventory | null | undefined): boolean => {
  if (!appInventory) return false;

  const hasAppGroupValue = Boolean(appInventory.appgroup?.val);
  const hasBundleValues = Array.isArray(appInventory.app_bundle_id?.val) && appInventory.app_bundle_id.val.length > 0;
  const hasInventoryList = Boolean(appInventory.app_bundle_id?.inventorylist?.id);

  return hasAppGroupValue || hasBundleValues || hasInventoryList;
};

const isAppInventoryExcludeMode = (appInventory: TargetingAppInventory | null | undefined): boolean => {
  if (!appInventory) return false;

  const appGroupValPresent = Boolean(appInventory.appgroup?.val);

  const hasIncludedAppGroup = appGroupValPresent && appInventory.appgroup?.op === Intersect.INTERSECTS;

  const hasExcludedAppGroup = appGroupValPresent && appInventory.appgroup?.op === Intersect.NOT_INTERSECTS;

  const hasIncludedBundleItems =
    Array.isArray(appInventory.app_bundle_id?.val) &&
    appInventory.app_bundle_id.val.some(
      item => item?.op === ComparisonType.EQUALS || item?.op === ComparisonType.BEGINS_WITH
    );

  const hasExcludedBundleItems =
    Array.isArray(appInventory.app_bundle_id?.val) &&
    appInventory.app_bundle_id.val.some(
      item => item?.op === ComparisonType.DOES_NOT_EQUAL || item?.op === ComparisonType.DOES_NOT_BEGIN_WITH
    );

  const inventoryList = appInventory.app_bundle_id?.inventorylist;
  const hasIncludedInventoryList = Boolean(inventoryList?.id) && inventoryList?.type === 'allow';

  const hasInclude = hasIncludedAppGroup || hasIncludedBundleItems || hasIncludedInventoryList;
  const hasExclude = hasExcludedAppGroup || hasExcludedBundleItems;

  return hasExclude && !hasInclude;
};

export function TargetingReadOnlyList({
  targeting,
  packageState,
  validateLegacyViewFormat,
}: TargetingReadOnlyListProps): JSX.Element {
  const { t } = useTranslation();
  const { useCurrentInstanceState, isOaSuborg } = useTargetingContext();
  const { isBefInstance } = useCurrentInstanceState();
  const { isPub, isOa, isGrowthbookFeatureOn } = usePermissionContext();

  const isAttentionOn = isGrowthbookFeatureOn(ENABLED_TARGETING_ATTENTION);
  const isAttentionAllowed = isGrowthbookFeatureOn(ALLOWLIST_TARGETING_ATTENTION);
  const showIsAttention = isOa && isAttentionOn && isAttentionAllowed;

  const {
    [TargetingFields.METACATEGORY]: metacategory,
    [TargetingFields.EXCLUDE_INDIRECT]: excludeIndirectValue,
    [TargetingFields.INCLUDE_OPEN_BIDDING_INVENTORY]: includeOpenBiddingInventoryValue,
  } = packageState[PackageFields.TARGETING];

  const excludeMFAValue = metacategory?.[TargetingFields.EXCLUDE_MFA] || false;

  const inventoryFormat = packageState[PackageFields.TARGETING][TargetingFields.INVENTORY_FORMAT];

  const adPlacement = inventoryFormat?.[InventoryFormatTypeOption.AD_PLACEMENT]?.val as string | null | undefined;
  const distributionChannel = inventoryFormat?.[InventoryFormatTypeOption.DISTRIBUTION_CHANNEL]?.val;

  const format = adPlacement?.toLowerCase() ?? '';
  const channelValue = distributionChannel?.toLowerCase() ?? '';
  const channelParts = channelValue.split(',').map(part => part.trim());

  const banner = AdPlacement.Banner.toLowerCase();
  const video = AdPlacement.Video.toLowerCase();
  const web = DistributionChannel.Web.toLowerCase();

  const isBannerOrVideo = format === banner || format === video;
  const isWeb = channelParts.includes(web);
  const envValidForIq = isWeb && isBannerOrVideo;

  const iqChecked = !!packageState[PackageFields.TARGETING]?.[TargetingFields.INVENTORY_QUALITY];

  const shouldShowExcludeBelowAverageDomains = !isPub && (envValidForIq || iqChecked);
  const shouldRenderExcludeBelowAverageDomainsInReadonly = shouldShowExcludeBelowAverageDomains && iqChecked;

  const isNewFormat =
    !packageState[PackageFields.TARGETING][TargetingFields.FORMAT_TYPE]?.[InventoryFormatTypeOption.AD_PLACEMENT]
      ?.val &&
    !packageState[PackageFields.TARGETING][TargetingFields.FORMAT_TYPE]?.[
      InventoryFormatTypeOption.DISTRIBUTION_CHANNEL
    ]?.val &&
    !packageState[PackageFields.TARGETING][TargetingFields.TECHNOLOGY_AND_DEVICES]?.[
      TechnologyAndDevicesOption.DEVICE_TYPE
    ]?.val;

  const noTargetingApplied =
    !targeting?.length && !excludeIndirectValue && !excludeMFAValue && inventoryFormat === null;

  const showInventoryQualitySubsection =
    (metacategory && excludeMFAValue) ||
    shouldRenderExcludeBelowAverageDomainsInReadonly ||
    (isBefInstance && excludeIndirectValue);

  const domainsTargeting = packageState[PackageFields.TARGETING][TargetingFields.URL_TARGETING];
  const appInventoryTargeting = packageState[PackageFields.TARGETING][TargetingFields.APP_INVENTORY] as
    | TargetingAppInventory
    | null
    | undefined;

  const domainsHaveValues = hasDomainsValues(domainsTargeting);
  const appInvHaveValues = hasAnyAppInventoryValues(appInventoryTargeting);

  const domainsAreExclude = isDomainsExcludeMode(domainsTargeting);
  const appInvIsExclude = isAppInventoryExcludeMode(appInventoryTargeting);

  const computedDomainAppOperator: DomainAppInventoryOperator | undefined =
    domainsHaveValues && appInvHaveValues
      ? !domainsAreExclude && !appInvIsExclude
        ? DomainAppInventoryOperator.OR
        : DomainAppInventoryOperator.AND
      : undefined;

  const domainAppInventoryInfoText = t(
    'Domains and App Inventory represent separate environments (web vs. app). Using OR ensures the deal can serve on either web domains or apps, instead of unintentionally requiring both and severely limiting eligible inventory.'
  );

  const shouldShowDomainAppInventoryInfo = computedDomainAppOperator === DomainAppInventoryOperator.OR;

  if (noTargetingApplied) {
    return <NoFiltersInfoAlert />;
  }

  const orPairWrapperSx = {
    position: 'relative',
    pl: 2.5,
    ml: 1,
    '&:before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 8,
      bottom: 8,
      width: '2px',
      borderRadius: '2px',
      backgroundColor: 'divider',
    },
  } as const;

  const getTargetingParams = (field: TargetingConfig['field']) => {
    let targetingParams = packageState[PackageFields.TARGETING][field];

    if (field === TargetingFields.CUSTOM_VARIABLES) {
      // `contextual` is only read by the Custom Targeting card that also shows contextual segments (flag off)
      targetingParams = toCustomTargetingParams(
        packageState[PackageFields.TARGETING][TargetingFields.CUSTOM_VARIABLES],
        packageState[PackageFields.TARGETING][TargetingFields.CONTEXTUAL_SEGMENTS]
      );
    }

    return targetingParams;
  };

  const elements: JSX.Element[] = [];

  for (let index = 0; index < targeting.length; index++) {
    const { field, name, TargetingComponent } = targeting[index];

    const shouldShowChip = index < targeting.length - 1;

    const isFromCurated = packageState.is_curated && isOaSuborg && packageState.account_uid === OPEN_AUDIENCE_ORG_UID;

    const nextField = targeting[index + 1]?.field;
    const nextItem = targeting[index + 1];

    const isBetweenDomainsAndAppInventory =
      field === TargetingFields.URL_TARGETING && nextField === TargetingFields.APP_INVENTORY;

    const chipDimension =
      isBetweenDomainsAndAppInventory && computedDomainAppOperator
        ? computedDomainAppOperator
        : packageState.targeting.dimension;

    const isOrChip = String(chipDimension) === Criteria.ANY;

    const isOrPair = isBetweenDomainsAndAppInventory && computedDomainAppOperator === DomainAppInventoryOperator.OR;

    if (isOrPair && nextItem) {
      elements.push(
        <Box key={`${field}-${nextField}-or-pair`} sx={orPairWrapperSx} data-test="or-pair-wrapper">
          <TargetingComponent
            name={name}
            field={field}
            targetingParams={getTargetingParams(field)}
            accountUid={packageState.account_uid}
            isFromCurated={isFromCurated}
            readonly
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DimensionChip
              targetingDimension={DomainAppInventoryOperator.OR}
              sx={{
                bgcolor: 'error.main',
                borderColor: 'error.main',
                color: 'primary.contrastText',
              }}
            />

            {shouldShowDomainAppInventoryInfo && (
              <Tooltip
                placement="right"
                slotProps={{
                  tooltip: {
                    sx: { maxWidth: 560, p: 2 },
                  },
                }}
                title={
                  <Box>
                    <Typography variant="body2">{domainAppInventoryInfoText}</Typography>
                  </Box>
                }
              >
                <IconButton size="small" aria-label={t('Domains and App Inventory info')} sx={{ ml: 0.5 }}>
                  <InfoRounded fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          <nextItem.TargetingComponent
            name={nextItem.name}
            field={nextItem.field}
            targetingParams={getTargetingParams(nextItem.field)}
            accountUid={packageState.account_uid}
            isFromCurated={isFromCurated}
            readonly
          />
        </Box>
      );

      index += 1;
      continue;
    }

    elements.push(
      <Fragment key={field}>
        <TargetingComponent
          name={name}
          field={field}
          targetingParams={getTargetingParams(field)}
          accountUid={packageState.account_uid}
          isFromCurated={isFromCurated}
          readonly
        />

        {shouldShowChip && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DimensionChip
              targetingDimension={chipDimension}
              sx={
                isOrChip
                  ? {
                      bgcolor: 'error.main',
                      borderColor: 'error.main',
                      color: 'primary.contrastText',
                    }
                  : undefined
              }
            />

            {isBetweenDomainsAndAppInventory && shouldShowDomainAppInventoryInfo && (
              <Tooltip
                placement="right"
                slotProps={{
                  tooltip: {
                    sx: { maxWidth: 560, p: 2 },
                  },
                }}
                title={
                  <Box>
                    <Typography variant="body2">{domainAppInventoryInfoText}</Typography>
                  </Box>
                }
              >
                <IconButton size="small" aria-label={t('Domains and App Inventory info')} sx={{ ml: 0.5 }}>
                  <InfoRounded fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      </Fragment>
    );
  }

  return (
    <>
      {!isNewFormat && validateLegacyViewFormat && (
        <RboDisplayedAlertProvider>
          <DisableRboAlert />
          <RboGroupValidationAlert />
        </RboDisplayedAlertProvider>
      )}

      {isNewFormat && (
        <StyledTargetingSubSectionTitle data-test="core-targeting-settings-title" sx={{ mt: 1.5 }}>
          {t('Core Targeting')}
        </StyledTargetingSubSectionTitle>
      )}

      {isNewFormat && (
        <RequiredTargetingSection
          key={`${adPlacement ?? ''}|${distributionChannel ?? ''}|${inventoryFormat?.device_type?.desktop_devices ?? ''}|${inventoryFormat?.device_type?.mobile_devices ?? ''}|${inventoryFormat?.device_type?.tv_devices ?? ''}`}
          readOnly
          targetingParams={inventoryFormat}
        />
      )}

      {showInventoryQualitySubsection && (
        <Grid
          container
          sx={{
            alignItems: 'center',
            mb: 4,
          }}
        >
          <StyledTargetingSubSectionTitle data-test="targeting-settings-quality-filters-title">
            {t('Inventory Quality')}
          </StyledTargetingSubSectionTitle>

          <Grid
            container
            size={12}
            sx={{
              mt: 1.5,
            }}
          >
            {metacategory && <ExcludeMFA excludeMFAValue={excludeMFAValue} readonly />}

            {shouldRenderExcludeBelowAverageDomainsInReadonly && (
              <ExcludeBelowAverageDomains readonly value={iqChecked} showTooltip={false} />
            )}

            {isBefInstance && excludeIndirectValue && (
              <ExcludeIndirect
                excludeIndirectValue={excludeIndirectValue}
                includeOpenBiddingInventory={includeOpenBiddingInventoryValue}
                readonly
              />
            )}
          </Grid>
        </Grid>
      )}

      {showIsAttention && (
        <AttentionOverview
          attention={packageState[PackageFields.TARGETING][TargetingFields.ATTENTION]}
          vpvh={packageState[PackageFields.TARGETING][TargetingFields.VPVH]}
        />
      )}

      {targeting.length > 0 && (
        <StyledTargetingSubSectionTitle data-test="advanced-targeting-settings-title" sx={{ mt: 1.5 }}>
          {t('Advanced Targeting')}
        </StyledTargetingSubSectionTitle>
      )}

      {elements}
    </>
  );
}
